import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceDot,
} from 'recharts';
import type { ForecastChartPoint } from '../types';

interface ForecastChartProps {
  data: ForecastChartPoint[];
  height?: number;
  title?: string;
  unit?: string;
}

/**
 * Forecast chart overlay: actual readings + forecast line + quantile band + anomaly markers.
 *
 * - Solid blue line: actual sensor readings
 * - Dashed orange line: forecast mean
 * - Shaded orange area: q10-q90 prediction interval (80%)
 * - Red dots: anomaly markers where actual fell outside the band
 */
export default function ForecastChart({
  data,
  height = 350,
  title,
  unit = '°C',
}: ForecastChartProps) {
  const anomalyPoints = data.filter(d => d.is_anomaly);

  return (
    <div>
      {title && (
        <h3 className="text-sm font-medium text-muted-foreground mb-2">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              fontSize: 12,
            }}
            formatter={(value, name) => {
              if (value == null) return ['-', String(name)];
              return [`${Number(value).toFixed(2)} ${unit}`, String(name)];
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
          />

          {/* Quantile band (q10 - q90) rendered as a range area */}
          <Area
            dataKey="bandRange"
            stroke="none"
            fill="#f97316"
            fillOpacity={0.1}
            name="q10–q90 Band"
            legendType="none"
            isAnimationActive={false}
          />

          {/* Actual readings */}
          <Line
            dataKey="actual"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            name="Actual"
            connectNulls={false}
          />

          {/* Forecast mean */}
          <Line
            dataKey="forecast_mean"
            stroke="#f97316"
            strokeWidth={2}
            strokeDasharray="6 3"
            dot={false}
            name="Forecast"
            connectNulls={false}
          />

          {/* Anomaly markers */}
          {anomalyPoints.map((point, i) => (
            <ReferenceDot
              key={`anomaly-${i}`}
              x={point.label}
              y={point.actual ?? 0}
              r={5}
              fill="#ef4444"
              stroke="#fff"
              strokeWidth={2}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
